import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import React from 'react'
import ReactDOM from 'react-dom'

import { MapSettings } from '@types'

import { MapAPI, MarkerOptions } from '../../lib/types'

import { initReactify, loadYandexMapsScript } from './ymapsScriptLoader'

// TODO: Вынести в конфиг или .env
const YANDEX_API_KEY = '8f551079-b4be-4e47-b474-0a6f4569d50b'

interface YandexMarkerData {
  id: string
  latitude: number
  longitude: number
  options?: MarkerOptions
}

/**
 * Хук для работы с Yandex MapsConfigs.tsx
 * Реализует императивный MapAPI интерфейс через адаптер к декларативным React компонентам
 *
 * Адаптер: addMarker/removeMarker → state → рендер YandexMarker компонентов
 */
export const useMapYandex = (
  settings: MapSettings,
  onReady?: (api: MapAPI) => void,
  onError?: (error: Error) => void
) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [ymapsComponents, setYmapsComponents] = useState<any>(null)
  const [reactify, setReactify] = useState<any>(null)

  // ✅ ПРАВИЛЬНО: State для хранения маркеров (адаптер императивный → декларативный)
  const markersRef = useRef<Map<string, YandexMarkerData>>(new Map())
  const [markersVersion, setMarkersVersion] = useState(0)

  // Деструктурируем настройки для стабильных зависимостей
  const centerLng = settings.center[0]
  const centerLat = settings.center[1]
  const zoom = settings.zoom

  // ✅ ПРАВИЛЬНО: Императивный API (обновляет state, триггерит перерисовку)
  const addMarker = useCallback(
    (id: string, latitude: number, longitude: number, options?: MarkerOptions) => {
      if (!isInitialized) {
        console.warn('Yandex Map not ready yet')

        return
      }

      markersRef.current.set(id, { id, latitude, longitude, options })
      setMarkersVersion(v => v + 1) // Триггерим перерисовку
    },
    [isInitialized]
  )

  const removeMarker = useCallback((id: string) => {
    if (markersRef.current.delete(id)) {
      setMarkersVersion(v => v + 1)
    }
  }, [])

  const clearMarkers = useCallback(() => {
    markersRef.current.clear()
    setMarkersVersion(v => v + 1)
  }, [])

  const setCenter = useCallback((latitude: number, longitude: number, zoom?: number) => {
    // TODO: Реализовать через обновление location
    console.warn('setCenter not implemented for Yandex MapsConfigs.tsx yet')
  }, [])

  const setZoom = useCallback((zoom: number) => {
    // TODO: Реализовать через обновление location
    console.warn('setZoom not implemented for Yandex MapsConfigs.tsx yet')
  }, [])

  const getCenter = useCallback((): [number, number] | null => {
    return [centerLat, centerLng]
  }, [centerLat, centerLng])

  const getZoom = useCallback((): number | null => {
    return zoom
  }, [zoom])

  const isReady = useCallback(() => isInitialized, [isInitialized])

  // Мемоизируем MapAPI для стабильности
  const mapApi = useMemo<MapAPI>(
    () => ({
      addMarker,
      removeMarker,
      clearMarkers,
      setCenter,
      setZoom,
      getCenter,
      getZoom,
      isReady,
    }),
    [addMarker, removeMarker, clearMarkers, setCenter, setZoom, getCenter, getZoom, isReady]
  )

  // Загрузка Yandex MapsConfigs.tsx API
  useEffect(() => {
    let mounted = true
    console.log('🚀 [useMapYandex] Начало инициализации')

    const init = async () => {
      try {
        console.log('🔑 [useMapYandex] API Key:', YANDEX_API_KEY.substring(0, 8) + '...')

        // Загружаем скрипт из CDN
        await loadYandexMapsScript(YANDEX_API_KEY)
        console.log('✅ [useMapYandex] Скрипт загружен')

        if (!mounted) {
          console.log('⚠️ [useMapYandex] Компонент размонтирован после загрузки скрипта')

          return
        }

        // Инициализируем reactify
        const { reactify: reactifyObj, components } = await initReactify(React, ReactDOM)
        console.log('✅ [useMapYandex] Reactify инициализирован')

        if (!mounted) {
          console.log('⚠️ [useMapYandex] Компонент размонтирован после инициализации reactify')

          return
        }

        setReactify(reactifyObj)
        setYmapsComponents(components)
        setIsInitialized(true)
        console.log('✅ [useMapYandex] Инициализация завершена успешно!')
      } catch (error) {
        console.error('❌ [useMapYandex] Ошибка инициализации:', error)
        onError?.(error as Error)
      }
    }

    init()

    // Cleanup
    return () => {
      console.log('🧹 [useMapYandex] Cleanup')
      mounted = false
      markersRef.current.clear()
      setIsInitialized(false)
    }
  }, [onError])

  // Вызываем onReady когда API готов
  useEffect(() => {
    if (isInitialized && mapApi) {
      onReady?.(mapApi)
    }
  }, [isInitialized, mapApi, onReady])

  // Возвращаем всё необходимое для рендера
  return {
    isInitialized,
    ymapsComponents,
    reactify,
    markers: Array.from(markersRef.current.values()),
    markersVersion, // Для принудительной перерисовки при необходимости
    location: { center: [centerLng, centerLat] as [number, number], zoom },
  }
}
